import { motion } from 'framer-motion';
import { FRUIT_VISUAL, getRipenessStage, type TreeFruit } from './types';

interface FruitNodeProps {
  fruit: TreeFruit;
  x: number;
  y: number;
  index: number;
  onClick: (fruit: TreeFruit) => void;
  isDimmed?: boolean;
}

const RIPENESS_GLOW: Record<string, string> = {
  new: '0 0 8px 2px rgba(255, 255, 200, 0.4)',
  growing: '0 0 12px 3px rgba(255, 220, 140, 0.5)',
  ripe: '0 0 16px 4px rgba(255, 200, 80, 0.6)',
  golden: '0 0 20px 6px rgba(255, 180, 40, 0.8), 0 0 40px 10px rgba(255, 200, 60, 0.3)',
};

export function FruitNode({ fruit, x, y, index, onClick, isDimmed }: FruitNodeProps) {
  const visual = FRUIT_VISUAL[fruit.template] || FRUIT_VISUAL.custom;
  const ripeness = getRipenessStage(fruit.createdAt);

  return (
    <motion.button
      className="absolute flex items-center justify-center cursor-pointer z-20 group"
      style={{
        left: `${x}%`,
        top: `${y}%`,
        transform: 'translate(-50%, -50%)',
      }}
      initial={{ scale: 0, opacity: 0 }}
      animate={{
        scale: isDimmed ? 0.7 : 1,
        opacity: isDimmed ? 0.4 : 1,
      }}
      transition={{
        type: 'spring',
        stiffness: 200,
        damping: 15,
        delay: index * 0.08,
      }}
      whileHover={{ scale: isDimmed ? 0.85 : 1.25 }}
      whileTap={{ scale: 0.9 }}
      onClick={() => onClick(fruit)}
      title={`${fruit.recipientName} — ${visual.label}`}
    >
      {/* Glow ring */}
      <motion.div
        className="absolute rounded-full"
        style={{
          width: 44,
          height: 44,
          boxShadow: RIPENESS_GLOW[ripeness],
        }}
        animate={{
          boxShadow: [
            RIPENESS_GLOW[ripeness],
            RIPENESS_GLOW[ripeness].replace(/[\d.]+\)$/, (m) => `${parseFloat(m) * 1.4})`),
            RIPENESS_GLOW[ripeness],
          ],
        }}
        transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut' }}
      />

      {/* Fruit body */}
      <span
        className="text-2xl select-none drop-shadow-lg"
        style={{ filter: isDimmed ? 'grayscale(0.6)' : 'none' }}
      >
        {visual.emoji}
      </span>

      {/* Hover label */}
      <div className="absolute -top-8 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap bg-black/80 text-white text-xs px-2 py-1 rounded-lg">
        {fruit.recipientName}
      </div>
    </motion.button>
  );
}
